import { useMemo, useCallback, useEffect } from 'react';
import { Select } from '@iq/react-components';

const ModelPickerField = (props) => {
  const {
    disabled,
    readonly,
    required = true,
    formData: value,
    formContext,
    schema: {
      dependsOnFormField,
      components = [],
      default: defaultValue,
      isClearable,
      noOptionsMessage,
      placeholder: schemaPlaceholder,
      label,
      styles = {},
    },
    onChange,
    ...otherProps
  } = props;

  const selectedComponentId = formContext?.[dependsOnFormField];

  const selectedComponent = components.find((c) => c.id === selectedComponentId);

  const models = useMemo(() => {
    if (!selectedComponent?.models) return [];
    return selectedComponent.models.map((model) => ({
      value: model.modelId,
      label: model.customName || model.modelId,
    }));
  }, [selectedComponent]);

  useEffect(() => {
    if (value && selectedComponent) {
      const isValidModel = selectedComponent.models?.some((model) => model.modelId === value);
      if (!isValidModel) {
        onChange(null);
      }
    }
  }, [selectedComponentId, selectedComponent, value, onChange]);

  const theValue = useMemo(() => {
    if (value) {
      const model = models.find((m) => m.value === value);
      return model || null;
    }
    if (defaultValue) {
      const model = models.find((m) => m.value === defaultValue);
      return model || null;
    }
    return null;
  }, [value, defaultValue, models]);

  const setValues = useCallback(
    (selected) => {
      onChange(selected ? selected.value : null);
    },
    [onChange]
  );

  const getEmptyMsg = (text) => <div>{text}</div>;

  const overrideStyles = useMemo(
    () => ({
      container: (base) => ({ ...base, lineHeight: 'normal', ...styles.container }),
      singleValue: (base) => ({ ...base, lineHeight: 'normal', ...styles.singleValue }),
      control: (base) => ({ ...base, lineHeight: 'normal', ...styles.control }),
    }),
    [styles]
  );

  const isDisabled = disabled || !selectedComponentId || models.length === 0;

  const LabelComponent = ({ showRequired = true }) =>
    label && (
      <label className="control-label">
        {label}
        {required && showRequired && <span className="required">*</span>}
      </label>
    );

  return (
    <div className="model-picker-field">
      <LabelComponent />
      <Select
        {...otherProps}
        className="schema-select-widget"
        isMulti={false}
        isClearable={isClearable && !isDisabled}
        closeMenuOnSelect={true}
        isDisabled={isDisabled}
        readOnly={readonly || isDisabled}
        required={required}
        placeholder={schemaPlaceholder}
        menuPlacement="auto"
        value={theValue}
        onChange={isDisabled ? undefined : setValues}
        options={models}
        noOptionsMessage={() => getEmptyMsg(noOptionsMessage || 'No options')}
        styles={overrideStyles}
      />
    </div>
  );
};

export default ModelPickerField;
